import { Container, Graphics, Text } from 'pixi.js';
import type { IDebugLabel } from './IRenderer';
import { PixiOverlayHost } from './PixiOverlayHost';
import { Z_LAYERS } from './Z_LAYERS';

/**
 * PixiDebugLabel — IDebugLabel impl backed by a PIXI.Container holding a
 * Graphics rect (the dark BG) + a Text. Pixi's Text doesn't natively
 * support backgroundColor + padding, so we composite manually.
 *
 * The BG rect is sized + positioned each setText() based on the new
 * text's measured dimensions. Anchor is bottom-center (0.5, 1) so the
 * label sits above the consumer's anchor point.
 */
export class PixiDebugLabel implements IDebugLabel {
  private container: Container | null = null;
  private bg: Graphics | null = null;
  private text: Text | null = null;
  private destroyed = false;

  // Pending state buffered until ensureReady() resolves.
  private pendingX = 0;
  private pendingY = 0;
  private pendingText = '';
  private pendingVisible = true;

  // Padding matches Phaser style padding {x:4, y:2}
  private static readonly PAD_X = 4;
  private static readonly PAD_Y = 2;

  constructor() {
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      this.build();
    });
  }

  private build(): void {
    const container = new Container();
    container.x = this.pendingX;
    container.y = this.pendingY;
    container.zIndex = Z_LAYERS.WORLD_DEBUG_LABEL;
    container.visible = this.pendingVisible;

    const bg = new Graphics();
    container.addChild(bg);

    const text = new Text({
      text: this.pendingText,
      style: {
        fontFamily: 'monospace',
        fontSize: 12,
        fill: 0xFFFF00,
        stroke: { color: 0x000000, width: 2 },
        align: 'center',
        lineHeight: 14, // approx Phaser lineSpacing 2 (12px font + 2)
      },
    });
    // Anchor (0.5, 1): center horizontally, bottom-aligned vertically.
    // We position the text at (0, -PAD_Y) so the BG rect can extend
    // padding below the text baseline.
    text.anchor.set(0.5, 1);
    container.addChild(text);

    PixiOverlayHost.addWorldChild(container);

    this.container = container;
    this.bg = bg;
    this.text = text;
    this.redrawBg();
  }

  private redrawBg(): void {
    if (!this.bg || !this.text) return;
    this.bg.clear();
    if (!this.text.text) return;

    const w = this.text.width + PixiDebugLabel.PAD_X * 2;
    const h = this.text.height + PixiDebugLabel.PAD_Y * 2;
    // Anchor (0.5, 1) → text spans x=[-w/2, w/2], y=[-h, 0]
    // Pad: rect should cover x=[-w/2, w/2], y=[-h, 0].
    this.bg.rect(-w / 2, -h, w, h).fill({ color: 0x000000, alpha: 0.8 });
  }

  setVisible(visible: boolean): void {
    this.pendingVisible = visible;
    if (this.container) this.container.visible = visible;
  }

  setPosition(x: number, y: number): void {
    this.pendingX = x;
    this.pendingY = y;
    if (this.container) {
      this.container.x = x;
      this.container.y = y;
    }
  }

  setText(text: string): void {
    if (this.pendingText === text) return;
    this.pendingText = text;
    if (this.text) {
      this.text.text = text;
      this.redrawBg();
    }
  }

  setAlpha(alpha: number): void {
    if (this.container) this.container.alpha = alpha;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
      this.bg = null;
      this.text = null;
    }
  }
}
